/**
 * ProtectedRoute
 *
 * Wraps a route that requires specific permissions. If the user does not
 * have access, renders an access denied message instead of the page.
 *
 * Usage in App.tsx:
 *   <Route
 *     path="/programs/create"
 *     element={
 *       <ProtectedRoute canAccess={canCreateProgram(user)}>
 *         <CreateProgram />
 *       </ProtectedRoute>
 *     }
 *   />
 */

import React from 'react';
import { Box, Button, SpaceBetween } from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';

interface ProtectedRouteProps {
  /** Whether the current user is allowed to access this route. */
  canAccess: boolean;
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ canAccess, children }) => {
  const navigate = useNavigate();

  if (!canAccess) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <SpaceBetween size="m" alignItems="center">
          <Box variant="h2">Access denied</Box>
          <Box variant="p" color="text-body-secondary">
            You don't have permission to access this page.
          </Box>
          <Button onClick={() => navigate(-1)}>Go back</Button>
        </SpaceBetween>
      </Box>
    );
  }

  return <>{children}</>;
};
